import React, { useEffect, useState } from "react";
import api, { API } from "../lib/api";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Markdown, StatusBadge, ScoreRing, DataLabel, LANG_LABEL } from "./common";
import VersionHistory from "./VersionHistory";
import { Check, X, ArrowsClockwise, PencilSimple, FloppyDisk, Code, LinkSimple, Books, Rocket, Image as ImageIcon } from "@phosphor-icons/react";

export default function ContentDetail({ contentId, onChanged }) {
  const [c, setC] = useState(null);
  const [lang, setLang] = useState("es");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/content/${contentId}`);
    setC(data);
    const langs = Object.keys(data.translations || {});
    if (langs.length && !langs.includes(lang)) setLang(data.primary_language || langs[0]);
  };
  useEffect(() => { load(); }, [contentId]);

  if (!c) return <div className="p-8 text-sm text-muted-foreground font-mono">Chargement…</div>;
  const langs = Object.keys(c.translations || {});
  const tr = c.translations?.[lang] || {};

  const startEdit = () => { setDraft({ ...tr }); setEditing(true); };
  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.put(`/content/${c.id}`, { translations: { [lang]: draft } });
      toast.success("Modifications enregistrées");
      setEditing(false); await load(); onChanged && onChanged();
    } catch (e) { toast.error("Échec de l'enregistrement"); } finally { setBusy(false); }
  };

  const redeploy = async () => {
    setBusy(true);
    try {
      await api.post(`/content/${c.id}/redeploy`);
      toast.success("Remis en file de déploiement", { description: "Allez dans Publication et cliquez « Déclencher le déploiement »." });
      await load(); onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action impossible"); } finally { setBusy(false); }
  };

  const validate = async (decision, publish = true, regenerate = false) => {
    setBusy(true);
    const MSG = { approve: publish ? "Contenu publié" : "Contenu approuvé",
      reject: regenerate ? "Article refusé — recherche d'un nouvel article en cours…" : "Article refusé — aucune régénération",
      request_changes: "Correction demandée", keep_draft: "Conservé en brouillon" };
    try {
      await api.post(`/content/${c.id}/validate`, { decision, publish, regenerate });
      toast.success(MSG[decision] || "Enregistré",
        regenerate ? { description: "Un nouvel article arrivera dans la file de validation d'ici quelques minutes." } : undefined);
      await load(); onChanged && onChanged();
    } catch (e) { toast.error("Action impossible"); } finally { setBusy(false); }
  };

  const proposeRevision = async () => {
    setBusy(true);
    try {
      await api.post(`/content/${c.id}/propose-revision`);
      toast.success("Révision IA lancée", { description: "Une version mise à jour arrivera dans la file de validation (~1-2 min)." });
      onChanged && onChanged();
    } catch (e) { toast.error("Impossible de lancer la révision"); } finally { setBusy(false); }
  };

  const regenerateImage = async () => {
    setBusy(true);
    try {
      await api.post(`/content/${c.id}/generate-image?count=3`);
      toast.success("3 visuels proposés — choisissez celui que vous préférez");
      await load(); onChanged && onChanged();
    } catch (e) { toast.error("Génération d'images impossible"); } finally { setBusy(false); }
  };

  const selectImage = async (idx) => {
    setBusy(true);
    try {
      await api.post(`/content/${c.id}/select-image?i=${idx}`);
      toast.success("Visuel sélectionné");
      await load(); onChanged && onChanged();
    } catch (e) { toast.error("Sélection impossible"); } finally { setBusy(false); }
  };

  const ver = (c.updated_at || "").replace(/\D/g, "").slice(0, 14) || "1";
  const coverUrl = c.image_path ? `${API}/public/media/${c.id}?v=${ver}` : null;
  const candidates = c.image_candidates || [];

  if (c.status === "generating")
    return <div className="p-10 text-center">
      <div className="inline-flex items-center gap-2 text-ai font-mono text-sm ai-pulse"><ArrowsClockwise size={18} /> Génération IA en cours…</div>
      <p className="text-xs text-muted-foreground mt-2">Rédaction multilingue et optimisation SEO/GEO.</p>
    </div>;

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
      {/* Article */}
      <div className="xl:col-span-2">
        <div className="flex items-center justify-between mb-3">
          <StatusBadge status={c.status} />
          <div className="flex items-center gap-2">
            {!editing ? (
              <button data-testid="edit-content-btn" onClick={startEdit} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-brand transition-colors"><PencilSimple size={15} /> Éditer</button>
            ) : (
              <button data-testid="save-content-btn" onClick={saveEdit} disabled={busy} className="flex items-center gap-1.5 text-xs text-brand font-medium"><FloppyDisk size={15} /> Enregistrer</button>
            )}
          </div>
        </div>

        <Tabs value={lang} onValueChange={(v) => { setLang(v); setEditing(false); }}>
          <TabsList data-testid="lang-tabs">
            {langs.map((l) => <TabsTrigger key={l} value={l} data-testid={`lang-tab-${l}`}>{LANG_LABEL[l] || l}</TabsTrigger>)}
          </TabsList>
          {c.sources?.length > 0 && (
            <div data-testid="trust-badge" className="inline-flex items-center gap-2 mt-4 bg-brand text-white rounded-full px-3.5 py-1.5 text-[13px] font-medium">
              <Check size={14} weight="bold" />
              Vérifié par le Dr Florian A. Vallecillo Cabrera · {c.sources.length} source{c.sources.length > 1 ? "s" : ""}
            </div>
          )}
          {langs.map((l) => {
            const t = c.translations[l] || {};
            const d = editing ? draft : t;
            return (
              <TabsContent key={l} value={l} className="mt-4">
                {editing ? (
                  <div className="space-y-3">
                    <input value={d.title || ""} onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                      className="w-full px-3 py-2 rounded-md border border-border font-serif text-xl text-brand" />
                    <input value={d.meta_title || ""} onChange={(e) => setDraft({ ...draft, meta_title: e.target.value })}
                      placeholder="Meta title" className="w-full px-3 py-2 rounded-md border border-border text-sm font-mono" />
                    <textarea value={d.meta_description || ""} onChange={(e) => setDraft({ ...draft, meta_description: e.target.value })}
                      rows={2} placeholder="Meta description" className="w-full px-3 py-2 rounded-md border border-border text-sm" />
                    <textarea value={d.body_markdown || ""} onChange={(e) => setDraft({ ...draft, body_markdown: e.target.value })}
                      rows={18} className="w-full px-3 py-2 rounded-md border border-border text-sm font-mono leading-relaxed" />
                  </div>
                ) : (
                  <article>
                    <h1 className="font-serif text-3xl text-brand leading-tight tracking-tight">{t.title}</h1>
                    {t.summary && <p className="mt-2 text-sm italic text-muted-foreground border-l-2 border-brand/30 pl-3">{t.summary}</p>}
                    <div className="mt-5"><Markdown text={t.body_markdown} /></div>
                    {t.faq?.length > 0 && (
                      <div className="mt-6 border-t border-border pt-4">
                        <DataLabel>FAQ</DataLabel>
                        <div className="mt-3 space-y-3">
                          {t.faq.map((q, i) => (
                            <div key={i}>
                              <div className="font-medium text-sm text-foreground">{q.q}</div>
                              <div className="text-sm text-muted-foreground mt-0.5">{q.a}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </article>
                )}
              </TabsContent>
            );
          })}
        </Tabs>
      </div>

      {/* SEO / GEO panel */}
      <aside className="space-y-5">
        <div className="bg-white border border-border rounded-md p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2"><ImageIcon size={14} className="text-muted-foreground" /><DataLabel>Image de l'article</DataLabel></div>
            <button data-testid="regenerate-image-btn" onClick={regenerateImage} disabled={busy}
              className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-brand transition-colors disabled:opacity-50">
              <ArrowsClockwise size={12} /> Proposer 3 visuels
            </button>
          </div>
          <div className="mt-3 rounded-md overflow-hidden aspect-[16/9] bg-secondary flex items-center justify-center">
            {coverUrl ? (
              <img src={coverUrl} alt={tr.title || ""} data-testid="content-cover-image" className="w-full h-full object-cover" />
            ) : (
              <div className="text-center text-muted-foreground px-4">
                <ImageIcon size={22} className="mx-auto mb-1 opacity-60" />
                <p className="text-[11px]">{busy ? "Génération des visuels…" : "Aucune image — cliquez « Proposer 3 visuels »"}</p>
              </div>
            )}
          </div>
          {candidates.length > 1 && (
            <div className="mt-3" data-testid="image-candidates">
              <p className="text-[11px] text-muted-foreground mb-2">Cliquez pour choisir le visuel à publier :</p>
              <div className="grid grid-cols-3 gap-2">
                {candidates.map((p, idx) => {
                  const isSel = p === c.image_path;
                  return (
                    <button key={idx} data-testid={`image-candidate-${idx}`} onClick={() => selectImage(idx)} disabled={busy}
                      className={`relative rounded overflow-hidden aspect-[16/9] bg-secondary transition-all disabled:opacity-50 ${
                        isSel ? "ring-2 ring-brand" : "ring-1 ring-border hover:ring-brand/50"}`}>
                      <img src={`${API}/public/media/${c.id}?i=${idx}&v=${ver}`} alt="" className="w-full h-full object-cover" />
                      {isSel && (
                        <span className="absolute top-1 right-1 bg-brand text-white rounded-full p-0.5"><Check size={10} weight="bold" /></span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="bg-white border border-border rounded-md p-5">
          <DataLabel>Scores</DataLabel>
          <div className="flex justify-around mt-4">
            <ScoreRing value={c.seo_score} label="SEO" accent="#0B3B2D" />
            <ScoreRing value={c.geo_score} label="GEO" accent="#2563EB" />
          </div>
        </div>

        <div className="bg-white border border-border rounded-md p-5">
          <DataLabel>Métadonnées · {LANG_LABEL[lang]}</DataLabel>
          <div className="mt-3 space-y-2 text-xs">
            <div><span className="text-muted-foreground">Meta title</span><div className="font-mono text-[11px] mt-0.5 break-words">{tr.meta_title || "—"}</div></div>
            <div><span className="text-muted-foreground">Meta description</span><div className="font-mono text-[11px] mt-0.5 break-words">{tr.meta_description || "—"}</div></div>
            <div><span className="text-muted-foreground">Slug</span><div className="font-mono text-[11px] mt-0.5 break-words">/{lang}/{tr.slug || "—"}</div></div>
          </div>
        </div>

        <div className="bg-white border border-border rounded-md p-5">
          <DataLabel>Aperçu avant publication</DataLabel>
          <div className="mt-3 border border-border rounded-md p-3 bg-[#fbfbfa]">
            <div className="text-[11px] text-[#5f6368]">clinicavalorian.es › {lang} › {tr.slug || "…"}</div>
            <div className="text-[#1a0dab] text-base leading-snug hover:underline cursor-pointer">{tr.meta_title || tr.title}</div>
            <div className="text-[13px] text-[#4d5156] leading-snug mt-0.5">{tr.meta_description || tr.summary}</div>
          </div>
          <div className="mt-3 border border-ai/30 bg-ai-soft rounded-md p-3">
            <div className="text-[10px] font-mono uppercase tracking-wider text-ai mb-1">Aperçu réponse IA (GEO)</div>
            <div className="text-[13px] text-foreground leading-snug">{tr.summary || "—"}</div>
            {c.sources?.length > 0 && (
              <div className="text-[10px] text-muted-foreground mt-1.5">Source : {c.sources[0].title?.slice(0, 60)} · Clínica Valorian</div>
            )}
          </div>
        </div>

        <div className="bg-white border border-border rounded-md p-5">
          <div className="flex items-center gap-2"><LinkSimple size={14} className="text-muted-foreground" /><DataLabel>Mots-clés</DataLabel></div>
          <div className="flex flex-wrap gap-1.5 mt-3">
            {(c.keywords || []).map((k, i) => <span key={i} className="text-[11px] font-mono bg-secondary px-2 py-0.5 rounded">{k}</span>)}
          </div>
        </div>

        {c.sources?.length > 0 && (
          <div className="bg-white border border-border rounded-md p-5">
            <div className="flex items-center gap-2"><Books size={14} className="text-muted-foreground" /><DataLabel>Sources scientifiques</DataLabel></div>
            <ul className="mt-3 space-y-2">
              {c.sources.map((s, i) => (
                <li key={i} className="text-xs"><a href={s.url} target="_blank" rel="noreferrer" className="text-ai hover:underline">{s.title}</a>
                  <span className="text-muted-foreground"> · {s.journal}</span></li>
              ))}
            </ul>
          </div>
        )}

        {c.internal_links?.length > 0 && (
          <div className="bg-white border border-border rounded-md p-5">
            <DataLabel>Maillage interne</DataLabel>
            <ul className="mt-3 space-y-1.5">
              {c.internal_links.map((l, i) => <li key={i} className="text-xs text-foreground">↳ {l.title}</li>)}
            </ul>
          </div>
        )}

        <details className="bg-white border border-border rounded-md p-5">
          <summary className="flex items-center gap-2 cursor-pointer"><Code size={14} className="text-muted-foreground" /><DataLabel>Données structurées (JSON-LD)</DataLabel></summary>
          <pre className="mt-3 text-[10px] font-mono bg-secondary rounded p-3 overflow-x-auto max-h-64">{JSON.stringify(c.jsonld, null, 2)}</pre>
        </details>

        {c.obsolescence?.is_obsolete && (
          <div className="bg-orange-50 border border-orange-200 rounded-md p-4 text-xs text-warn">
            <strong>À actualiser :</strong> {c.obsolescence.reason}
          </div>
        )}

        {c.changelog && (
          <div className="bg-ai-soft border border-ai/30 rounded-md p-4 text-xs text-foreground">
            <strong className="text-ai">Révision — changements :</strong> {c.changelog}
          </div>
        )}

        {["published", "needs_update"].includes(c.status) && (
          <button data-testid="propose-revision-btn" onClick={proposeRevision} disabled={busy}
            className="w-full flex items-center justify-center gap-2 border border-ai text-ai hover:bg-ai-soft py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-60">
            <ArrowsClockwise size={16} /> Proposer une révision IA
          </button>
        )}

        <VersionHistory contentId={c.id} primaryLang={c.primary_language || "es"} onChanged={onChanged} />

        {["pending_validation", "needs_update", "approved", "changes_requested", "draft"].includes(c.status) && (
          <div className="bg-white border border-border rounded-md p-5 space-y-3 sticky bottom-4">
            <DataLabel>Validation médicale — 3 choix</DataLabel>
            {/* 1. Publier */}
            <button data-testid="approve-publish-btn" onClick={() => validate("approve", true)} disabled={busy}
              className="w-full flex items-center justify-center gap-2 bg-brand hover:bg-brand-hover text-white py-2.5 rounded-md text-sm font-medium transition-colors disabled:opacity-60">
              <Check size={16} /> 1 · Valider et publier
            </button>
            {/* 2. Refuser et régénérer */}
            <button data-testid="reject-regenerate-btn" onClick={() => validate("reject", false, true)} disabled={busy}
              className="w-full flex items-center justify-center gap-2 border border-ai text-ai hover:bg-ai-soft py-2.5 rounded-md text-sm font-medium transition-colors disabled:opacity-60">
              <ArrowsClockwise size={16} /> 2 · Refuser et chercher un autre article
            </button>
            {/* 3. Refuser et arrêter */}
            <button data-testid="reject-stop-btn" onClick={() => validate("reject", false, false)} disabled={busy}
              className="w-full flex items-center justify-center gap-2 border border-red-200 text-red-600 hover:bg-red-50 py-2.5 rounded-md text-sm font-medium transition-colors disabled:opacity-60">
              <X size={16} /> 3 · Refuser sans régénérer (arrêter)
            </button>
            {/* Secondary actions */}
            <div className="pt-2 border-t border-border">
              <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-2">Autres actions</div>
              <div className="flex gap-2">
                <button data-testid="approve-only-btn" onClick={() => validate("approve", false)} disabled={busy}
                  className="flex-1 border border-border hover:bg-secondary py-2 rounded-md text-xs transition-colors">Approuver (sans publier)</button>
                <button data-testid="request-changes-btn" onClick={() => validate("request_changes", false)} disabled={busy}
                  className="flex-1 border border-purple-200 text-purple-700 hover:bg-purple-50 py-2 rounded-md text-xs transition-colors">Demander une correction</button>
                <button data-testid="keep-draft-btn" onClick={() => validate("keep_draft", false)} disabled={busy}
                  className="flex-1 border border-border hover:bg-secondary py-2 rounded-md text-xs transition-colors">Brouillon</button>
              </div>
            </div>
          </div>
        )}
        {c.status === "published" && (
          <div className="bg-white border border-border rounded-md p-5 sticky bottom-4" data-testid="published-actions">
            <DataLabel>Article publié</DataLabel>
            <div className="text-[11px] mt-1 mb-3" style={{ color: c.deployed ? "#166534" : "#D95D39" }}>
              {c.deployed ? "● Déployé (live dans le feed)" : "○ En attente de déploiement"}
              {c.live_verified ? " · vérifié en ligne sur le site" : ""}
            </div>
            <button data-testid="redeploy-btn" onClick={redeploy} disabled={busy}
              className="w-full flex items-center justify-center gap-2 border border-brand text-brand hover:bg-brand-soft py-2.5 rounded-md text-sm font-medium transition-colors disabled:opacity-60">
              <Rocket size={16} weight="fill" /> Remettre en file de déploiement
            </button>
            <p className="text-[11px] text-muted-foreground mt-2">Puis allez dans <b>Publication</b> → « Déclencher le déploiement » pour le renvoyer au site.</p>
          </div>
        )}
      </aside>
    </div>
  );
}
